import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { ICustomer } from "types/interfaces/customers/ICustomer";
import { Typography } from "@beamcloud/design-system";
import Logger from "services/logger";
import sanityClient from "./sanity";
import BannerSection from "../components/BannerSection";
import { useLocation } from "react-router-dom";
import CaseStudyCard from "./components/CaseStudyCard";
import { device } from "constants/Devices";
import { typography } from "../components/typography";

const Customers: React.FC = () => {
  const [allCustomers, setAllCustomers] = useState<ICustomer[]>([]);

  const location = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const customers: ICustomer[] = await sanityClient.fetch(
          `
          *[_type == "caseStudy"] | order(_createdAt desc) {
            "id": _id,
            title,
            description,
            company->{
              "id": _id,
              name,
              about,
              industry,
              link,
              "image": picture
            },
            coverImage,
            "slug": slug.current,
            testimonial->{
              "id": _id,
              name,
              position,
              testimonial,
              image
            },
            "figures": figures[]{
              "id": _key,
              metric,
              description
            }
          }
        `
        );
        setAllCustomers(customers);
      } catch (error) {
        Logger.error("fetchAllCustomers", error);
      }
    })();
  }, []);

  return (
    <Container>
      <TitleWrapper>
        <Helmet>
          <title>Customers • Beam</title>
          <link rel="canonical" href={`${window.location.origin}${location.pathname}`} />
          <meta
            name="description"
            content={
              "Ultrafast sandbox environments for AI agents. Bring your own custom image and run untrusted code safely – at scale."
            }
          />
          <meta property="og:type" content="website" />
          <meta property="og:title" content={`Customers • Beam`} />
          <meta
            property="og:description"
            content={
              "Ultrafast sandbox environments for AI agents. Bring your own custom image and run untrusted code safely – at scale."
            }
          />
          <meta property="og:image" content={"https://www.beam.cloud/meta-hero.png"} />
          <meta property="og:url" content={window.location.href} />
          <meta name="twitter:title" content={`Customers • Beam`} />
          <meta
            name="twitter:description"
            content={
              "Ultrafast sandbox environments for AI agents. Bring your own custom image and run untrusted code safely – at scale."
            }
          />
          <meta name="twitter:image" content={"https://www.beam.cloud/meta-hero.png"} />
          <meta name="twitter:card" content="summary_large_image" />
        </Helmet>
        <CustomerTag>Explore Success Stories</CustomerTag>
        <PageTitle>Customer Stories</PageTitle>
      </TitleWrapper>
      <Articles $count={allCustomers.length}>
        {allCustomers.map(customer => (
          <CaseStudyCard key={customer.id} {...customer} />
        ))}
      </Articles>
      <BannerSection />
    </Container>
  );
};

export default Customers;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 64px;
  align-items: center;
  padding-top: 48px;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 640px;
  gap: 24px;
  padding-left: 10px;
  padding-right: 10px;
`;

const Articles = styled.div<{ $count: number }>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: stretch;
  justify-content: ${props => (props.$count === 1 ? "center" : "space-between")};
  padding: 0px 16px;
  gap: 32px;
  max-width: 1120px;
  width: 100%;

  @media ${device.mobileXL} {
    justify-content: center;
  }
`;

const PageTitle = styled.h1`
  ${typography.title60};
  color: ${props => props.theme.colors.gray100};
  margin: 0;
  text-align: center;
`;

const CustomerTag = styled.div`
  border-radius: 8px;
  padding: 2px 8px;
  background: rgba(62, 94, 221, 0.24);
  ${typography.textBaseLight};
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(132, 157, 255, 1);
`;
